/**
 * Web Crypto API encryption for localStorage health data.
 *
 * Uses AES-GCM (256-bit) with a key derived via PBKDF2 from the
 * authenticated user's ID.  A random salt is persisted in localStorage
 * (salt alone is not sensitive).  The IV is prepended to every
 * ciphertext so each write is unique.
 *
 * Threat model:
 *  – Physical / XSS access to localStorage yields only ciphertext.
 *  – Without the user-ID the key cannot be re-derived.
 *  – Combined with sign-out purge + inactivity logout the window of
 *    exposure is minimal.
 */

const SALT_KEY = "__crypto_salt";
const PBKDF2_ITERATIONS = 100_000;
const ENC_PREFIX = "enc:";

// ── module state ────────────────────────────────────────────────────
let _encryptionKey: CryptoKey | null = null;
let _ready = false;

// The set of localStorage keys that contain sensitive health data.
export const SENSITIVE_KEYS = new Set([
  "periodDates",
  "symptoms",
  "mood",
  "flowIntensity",
  "bbtTemperatures",
  "notes",
  "medications",
  "contraception",
  "customSymptoms",
  "goals",
  "wellness",
  "hydrationWeight",
  "notifications",
  "pregnancyData",
  "menopauseData",
  "menopauseLogs",
  "pregnancyLogs",
  "healthkit_data",
]);

// ── helpers ─────────────────────────────────────────────────────────

function getOrCreateSalt(): Uint8Array {
  const existing = localStorage.getItem(SALT_KEY);
  if (existing) {
    return Uint8Array.from(atob(existing), (c) => c.charCodeAt(0));
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  localStorage.setItem(SALT_KEY, btoa(String.fromCharCode(...salt)));
  return salt;
}

async function deriveKey(userId: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const rawBytes = encoder.encode(userId);
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    rawBytes.buffer as ArrayBuffer,
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: salt.buffer as ArrayBuffer, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

// ── public API ──────────────────────────────────────────────────────

/** Initialise encryption for the current user.  Call once after login. */
export async function initEncryption(userId: string): Promise<void> {
  if (!userId) return;
  try {
    const salt = getOrCreateSalt();
    _encryptionKey = await deriveKey(userId, salt);
    _ready = true;
    // Migrate any existing unencrypted sensitive data
    await migrateUnencryptedData();
  } catch (err) {
    console.error("Failed to initialise encryption:", err);
    _ready = false;
  }
}

/** Tear down – called on sign-out. */
export function clearEncryption(): void {
  _encryptionKey = null;
  _ready = false;
}

/** Whether encryption is currently active. */
export function isEncryptionReady(): boolean {
  return _ready && _encryptionKey !== null;
}

/** Whether a given key should be encrypted. */
export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(key);
}

/** Encrypt a plaintext string → base64 ciphertext prefixed with `enc:`. */
export async function encryptValue(plaintext: string): Promise<string> {
  if (!_encryptionKey) throw new Error("Encryption not initialised");
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const cipherBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    _encryptionKey,
    encoded.buffer as ArrayBuffer
  );
  // Concatenate iv + ciphertext, then base64-encode
  const combined = new Uint8Array(iv.length + cipherBuf.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(cipherBuf), iv.length);
  return ENC_PREFIX + btoa(String.fromCharCode(...combined));
}

/** Decrypt a value previously produced by `encryptValue`. */
export async function decryptValue(stored: string): Promise<string> {
  if (!_encryptionKey) throw new Error("Encryption not initialised");
  if (!stored.startsWith(ENC_PREFIX)) {
    // Not encrypted – return as-is (handles migration gracefully)
    return stored;
  }
  const raw = atob(stored.slice(ENC_PREFIX.length));
  const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
  const iv = bytes.slice(0, 12);
  const ciphertext = bytes.slice(12);
  const plainBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    _encryptionKey,
    ciphertext.buffer as ArrayBuffer
  );
  return new TextDecoder().decode(plainBuf);
}

/** Check whether a stored value is already encrypted. */
export function isEncrypted(value: string): boolean {
  return value.startsWith(ENC_PREFIX);
}

/**
 * One-time migration: re-encrypt any existing plaintext sensitive keys.
 * Runs automatically when encryption is initialised.
 */
async function migrateUnencryptedData(): Promise<void> {
  if (!_encryptionKey) return;
  for (const key of SENSITIVE_KEYS) {
    const raw = localStorage.getItem(key);
    if (raw && !isEncrypted(raw)) {
      try {
        const encrypted = await encryptValue(raw);
        localStorage.setItem(key, encrypted);
      } catch {
        // If encryption fails for a key, leave it as-is rather than losing data
        console.warn(`Migration: could not encrypt ${key}`);
      }
    }
  }
}
